import { useState, useRef, useCallback, useEffect } from 'react';
import queryString from 'query-string';
import createRestApiClient from '../../utils/createRestApiClient';
import { getCookieData } from '../../utils/cookieHandler/handler';
import { getEnvVar } from '../../utils/env';
import { POLLING_INTERVAL_MS, MAX_POLL_COUNT } from './constants';
import { getProductRCA } from './helpers';
import { RCA_EVENTS } from './events';

const getAuthHeaders = (cookieName) => {
  const name = cookieName || getEnvVar('REACT_APP_COOKIE_NAME');
  const accessToken = getCookieData(name);
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
};

const getClient = (apiBaseUrl) =>
  createRestApiClient().withConfig({ baseURL: apiBaseUrl }).client;

const fetchRCA = async (
  apiBaseUrl,
  testId,
  { product, trigger, retry, userId, cookieName, extraQueryParams } = {}
) => {
  const client = getClient(apiBaseUrl);
  const { rcaProduct, ...restQueryParams } = extraQueryParams || {};
  const query = queryString.stringify(
    {
      product: rcaProduct || product,
      trigger: trigger || undefined,
      retry: retry || undefined,
      execution_user_id: userId || undefined,
      ...restQueryParams,
    },
    { skipNull: true, skipEmptyString: true }
  );
  const url = `/insights/api/v3/rca/${testId}${query ? `?${query}` : ''}`;
  const response = await client.get(url, {
    headers: getAuthHeaders(cookieName),
  });
  return response?.data;
};

const submitRCAFeedback = async (
  apiBaseUrl,
  testId,
  product,
  payload,
  cookieName
) => {
  const client = getClient(apiBaseUrl);
  const query = queryString.stringify(
    { product },
    { skipNull: true, skipEmptyString: true }
  );
  const url = `/insights/api/v3/rca/${testId}${query ? `?${query}` : ''}`;
  const response = await client.put(url, payload, {
    headers: getAuthHeaders(cookieName),
  });
  return response?.data;
};

const fetchErrorRepetition = async (apiBaseUrl, payload, cookieName) => {
  const client = getClient(apiBaseUrl);
  const response = await client.post(
    '/insights/api/v3/tms-report/rca-category',
    payload,
    { headers: getAuthHeaders(cookieName) }
  );
  return response?.data;
};

const fetchErrorTrends = async (apiBaseUrl, payload, cookieName) => {
  const client = getClient(apiBaseUrl);
  const response = await client.post(
    '/insights/api/v3/tms-report/rca-category-trends',
    payload,
    { headers: getAuthHeaders(cookieName) }
  );
  return response?.data;
};

/**
 * Shared hook for AI RCA data fetching, polling, analytics, and feedback.
 * Used by all products: Web Automation, App Automation, HyperExecute, KaneAI.
 *
 * @param {object} config
 * @param {string} config.testId - Test ID to fetch RCA for
 * @param {string} config.product - 'automation' | 'app-automation' | 'hyperexecute'
 * @param {string} config.apiBaseUrl - Base URL for insights API
 * @param {string|number} [config.userId] - User ID for RCA trigger
 * @param {string} [config.testName] - Test name for analytics queries
 * @param {string} [config.createdAt] - Test creation timestamp (ISO) for analytics
 * @param {string} [config.rcaCategory] - Pre-existing RCA category (for analytics on drawer open)
 * @param {boolean} [config.enabled=false] - Whether to auto-fetch on mount
 */
const useRCA = ({
  testId,
  product,
  apiBaseUrl,
  userId,
  testName,
  createdAt,
  rcaCategory,
  cookieName,
  extraQueryParams,
  extraAnalyticsParams,
  isTestManager,
  onTrack,
  enabled = false,
}) => {
  // Held in a ref so firing analytics never invalidates the fetch/poll callbacks.
  const trackRef = useRef(onTrack);
  useEffect(() => {
    trackRef.current = onTrack;
  }, [onTrack]);
  const track = useCallback((name, params) => {
    trackRef.current?.(name, params);
  }, []);
  const [rcaData, setRcaData] = useState(null);
  // Start loading when auto-fetch is enabled so render #1 reflects the pending fetch (runs a tick later in an effect).
  const [isLoading, setIsLoading] = useState(enabled);
  // True only while an RCA is actively being generated (trigger + polling) —
  // drives the "Generating RCA" progress loader. A plain fetch of an existing
  // RCA keeps this false so the drawer shows the skeleton loader instead.
  const [isGenerating, setIsGenerating] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isNotEnoughCredit, setIsNotEnoughCredit] = useState(false);

  const [errorRepetitionData, setErrorRepetitionData] = useState(null);
  const [errorTrendsData, setErrorTrendsData] = useState(null);

  const pollingRef = useRef(null);
  const pollCountRef = useRef(0);
  const mountedRef = useRef(true);
  // True while a generation is in flight; gates airca_generation_succeeded to real generations only.
  const generatingRef = useRef(false);
  // Holds startPolling so doFetch can (re)start the poll loop without a circular
  // useCallback dependency (doFetch ⇄ startPolling).
  const startPollingRef = useRef(null);

  // Keep extraQueryParams in a ref so async callbacks (fetchAnalytics, doFetch)
  // always read the latest value without invalidating useCallback closures.
  // This handles the case where testDetail loads async after the hook initializes.
  const extraQueryParamsRef = useRef(extraQueryParams);
  useEffect(() => {
    extraQueryParamsRef.current = extraQueryParams;
  }, [extraQueryParams]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    pollCountRef.current = 0;
    // Generation is no longer in flight once polling stops (success, error, or
    // timeout). triggerRCA re-asserts isGenerating right after it calls this.
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const fetchAnalytics = useCallback(
    async (category) => {
      if (!apiBaseUrl || !testName) return;
      try {
        const currentExtraQueryParams = extraQueryParamsRef.current;
        let isHyperExecute = product === 'hyperexecute' || currentExtraQueryParams?.rcaProduct === 'hyperexecute';
        let analyticsProduct = product;
        if (isTestManager) {
          //In case of Test Manager, we don't send the product to use the default index and is_hyperexecute should be false
          isHyperExecute = false;
          analyticsProduct = undefined;
        } else if (isHyperExecute) {
          analyticsProduct = getProductRCA({ testId });
        }
        const repetitionPayload = {
          test_name: testName,
          created_at: createdAt,
          product: analyticsProduct,
          is_hyperexecute: isHyperExecute,
          ...extraAnalyticsParams,
        };
        const trendsPayload = {
          test_name: testName,
          created_at: createdAt,
          rca_category: category,
          product: analyticsProduct,
          is_hyperexecute: isHyperExecute,
          ...extraAnalyticsParams,
        };
        const [repResult, trendResult] = await Promise.allSettled([
          fetchErrorRepetition(apiBaseUrl, repetitionPayload, cookieName),
          fetchErrorTrends(apiBaseUrl, trendsPayload, cookieName),
        ]);
        if (mountedRef.current) {
          const repResponse = repResult.status === 'fulfilled' ? repResult.value : null;
          const trendResponse = trendResult.status === 'fulfilled' ? trendResult.value : null;

          if (repResult.status === 'rejected') {
            console.warn('[AIRca] Error repetition fetch failed:', repResult.reason);
          }
          if (trendResult.status === 'rejected') {
            console.warn('[AIRca] Error trends fetch failed:', trendResult.reason);
          }

          setErrorRepetitionData(repResponse?.data ?? repResponse ?? null);
          setErrorTrendsData(trendResponse?.data ?? trendResponse ?? null);
        }
      } catch {
        // Analytics are non-critical; silently fail
      }
    },
    [apiBaseUrl, testName, createdAt, cookieName, extraAnalyticsParams, product, testId, isTestManager]
  );

  // Defense-in-depth: the backend normally returns a failed RCA as a 5xx/4xx
  // (handled in doFetch's catch). But if it ever reports a terminal failure in
  // a 200 body (status:"failed"/"error"), detect it here so polling stops and
  // the error surfaces instead of spinning to the timeout.
  const getTerminalFailure = useCallback((data) => {
    const payload = data?.data || data;
    const status = String(payload?.status || '').toLowerCase();
    if (status === 'failed' || status === 'error') {
      return payload?.remark || payload?.message || 'RCA generation failed.';
    }
    return null;
  }, []);

  // True when the response is an "in progress" signal (generation running),
  // as opposed to "no data yet". parseRCAResponse maps both to null, so doFetch
  // uses this to resume the generating/polling state instead of showing Generate.
  const isInProgressResponse = useCallback(
    (data) =>
      data?.message === 'RCA triggered' ||
      data?.message === 'RCA already in progress',
    []
  );

  const parseRCAResponse = useCallback((data) => {
    if (!data) return null;

    // Check for "RCA triggered" or "RCA already in progress" messages
    if (
      data?.message === 'RCA triggered' ||
      data?.message === 'RCA already in progress'
    ) {
      return null; // Still generating, keep polling
    }

    const rcaPayload = data?.data || data;
    if (!rcaPayload?.raw_json) return null;

    try {
      const rawJson =
        typeof rcaPayload.raw_json === 'string'
          ? JSON.parse(rcaPayload.raw_json)
          : rcaPayload.raw_json;

      return {
        ...rcaPayload,
        raw_json: rawJson,
        category: rcaPayload.category,
      };
    } catch {
      return null;
    }
  }, []);

  const handleRCASuccess = useCallback(
    (parsed, { skipAnalytics = false } = {}) => {
      if (!mountedRef.current) return;
      // Capture before stopPolling() clears it — only real generations succeed.
      const wasGenerating = generatingRef.current;
      setRcaData(parsed.raw_json);
      setIsLoading(false);
      setIsError(false);
      setErrorMessage('');
      stopPolling();

      if (wasGenerating) {
        track(RCA_EVENTS.GENERATION_SUCCEEDED, {
          category: parsed.category || parsed.raw_json?.parent_failure_category,
          has_timeline: !!parsed.raw_json?.error_timeline,
          has_fixes: !!parsed.raw_json?.steps_to_fix,
        });
      }

      // Fetch analytics (skip when refetching after feedback)
      // Use rcaCategory prop (instance-level parent category) first — it matches
      // what the trends/repetition backend expects. Fall back to parsed data for
      // first-time generation where the prop is still empty.
      if (!skipAnalytics) {
        const category =
          rcaCategory ||
          parsed.category ||
          parsed.raw_json?.parent_failure_category;
        if (category) {
          fetchAnalytics(category);
        }
      }
    },
    [stopPolling, fetchAnalytics, rcaCategory, track]
  );

  const doFetch = useCallback(
    async ({
      trigger = false,
      retry = false,
      skipAnalytics = false,
      polling = false,
    } = {}) => {
      if (!testId || !apiBaseUrl) return;
      try {
        const response = await fetchRCA(apiBaseUrl, testId, {
          product,
          trigger: trigger ? true : undefined,
          retry: retry ? true : undefined,
          userId: userId ? Number(userId) : undefined,
          cookieName,
          extraQueryParams: extraQueryParamsRef.current,
        });

        if (!mountedRef.current) return;

        const parsed = parseRCAResponse(response);
        if (parsed) {
          handleRCASuccess(parsed, { skipAnalytics });
          return;
        }

        // Terminal failure reported in a 200 body — stop polling and surface it.
        const failureRemark = getTerminalFailure(response);
        if (failureRemark) {
          stopPolling();
          setIsError(true);
          setErrorMessage(failureRemark);
          setIsLoading(false);
          track(RCA_EVENTS.GENERATION_FAILED, { error_type: 'terminal_status' });
          return;
        }

        // Generation is in progress ("RCA triggered" / "already in progress").
        // Show the generating loader; if a plain fetch discovered an in-flight
        // generation (e.g. one started earlier, then the drawer reopened or the
        // user navigated back to this test), (re)start polling so it resolves.
        if (isInProgressResponse(response)) {
          if (mountedRef.current) {
            setIsGenerating(true);
            setIsLoading(true);
          }
          // Mark in-flight so the eventual poll completion counts as a success.
          generatingRef.current = true;
          if (!trigger && !polling && !pollingRef.current) {
            startPollingRef.current?.();
          }
          return;
        }

        // No data and not in progress — only the passive existence check
        // (neither trigger nor poll) stops loading so the Generate state shows.
        if (!trigger && !polling && mountedRef.current) {
          setIsLoading(false);
        }
      } catch (err) {
        if (!mountedRef.current) return;

        const status = err?.response?.status || err?.status;
        const apiError = String(err?.response?.data?.error || '').toLowerCase();
        const notReady = status === 404 || apiError.includes('not found');

        // The backend 404s ("RCA not found") until the RCA record is written —
        // generation is still in progress. This applies both to polls and to
        // the initial trigger call (which can 404 in the pre-row window if the
        // FE deploys before the BE reliability fix, LPA #2913). In either case
        // keep the loader up and let polling take over; do NOT surface a hard
        // error or revert to the Generate state. MAX_POLL_COUNT bounds it.
        if ((trigger || polling) && notReady) {
          return;
        }

        stopPolling();

        if (status === 402 && (trigger || polling)) {
          setIsNotEnoughCredit(true);
          setIsLoading(false);
          track(RCA_EVENTS.CREDIT_INSUFFICIENT);
        } else if (status === 402) {
          // Auto-fetch hit 402 — silently stop loading, don't show credit popup
          setIsLoading(false);
        } else if (status === 404 && !trigger) {
          // Initial check: RCA simply hasn't been generated yet → Generate state
          setIsLoading(false);
        } else {
          setIsError(true);
          setErrorMessage(
            err?.response?.data?.error || err?.message || 'Failed to fetch RCA'
          );
          setIsLoading(false);
          if (trigger || polling) {
            track(RCA_EVENTS.GENERATION_FAILED, {
              error_type: status === 404 ? 'not_found' : status ? `http_${status}` : 'network',
            });
          }
        }
      }
    },
    [
      testId,
      apiBaseUrl,
      product,
      userId,
      cookieName,
      parseRCAResponse,
      getTerminalFailure,
      isInProgressResponse,
      handleRCASuccess,
      stopPolling,
      track,
    ]
  );

  /**
   * Start the status-poll loop (setTimeout chaining to avoid overlapping
   * fetches). Polls use trigger:false — only the initial trigger generates;
   * polling with trigger:true would re-trigger (and re-fail) generation every
   * tick AND mask the backend's failed-status error. Reused by triggerRCA and
   * by doFetch when a plain fetch discovers an in-flight generation.
   */
  const startPolling = useCallback(() => {
    pollCountRef.current = 0;
    const scheduleNextPoll = () => {
      pollingRef.current = setTimeout(() => {
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLL_COUNT) {
          stopPolling();
          if (mountedRef.current) {
            setIsLoading(false);
            setIsError(true);
            setErrorMessage('RCA generation timed out. Please try again.');
          }
          track(RCA_EVENTS.POLLING_TIMEOUT, { poll_count: pollCountRef.current });
          return;
        }
        doFetch({ trigger: false, polling: true }).then(() => {
          if (pollingRef.current) scheduleNextPoll();
        });
      }, POLLING_INTERVAL_MS);
    };
    scheduleNextPoll();
  }, [doFetch, stopPolling, track]);

  // Expose startPolling via ref so doFetch can call it without a circular dep.
  useEffect(() => {
    startPollingRef.current = startPolling;
  }, [startPolling]);

  /**
   * Trigger RCA generation with polling.
   */
  const triggerRCA = useCallback(
    ({ retry = false } = {}) => {
      stopPolling();
      setIsLoading(true);
      setIsGenerating(true);
      // Mark generation active AFTER stopPolling (which clears it).
      generatingRef.current = true;
      setIsError(false);
      setIsNotEnoughCredit(false);
      setErrorMessage('');
      setRcaData(null);
      setErrorRepetitionData(null);
      setErrorTrendsData(null);

      track(RCA_EVENTS.GENERATION_REQUESTED, { retry: !!retry });
      doFetch({ trigger: true, retry });
      startPolling();
    },
    [doFetch, stopPolling, startPolling, track]
  );

  /**
   * Fetch existing RCA data (without triggering generation). Shows existing
   * data, or — if the backend reports generation already in progress — resumes
   * the loading state and (re)starts polling (see doFetch). Stops any prior
   * poll first so navigating between tests doesn't leave a stale loop running.
   */
  const fetchExistingRCA = useCallback(() => {
    stopPolling();
    setIsLoading(true);
    setIsGenerating(false);
    setIsError(false);
    setIsNotEnoughCredit(false);
    setRcaData(null);

    doFetch({ trigger: false });
  }, [doFetch, stopPolling]);

  /**
   * Re-fetch RCA data (e.g., after feedback submission).
   * Skips analytics re-fetch to preserve existing trends data.
   */
  const refetch = useCallback(() => {
    if (testId && apiBaseUrl) {
      doFetch({ trigger: false, skipAnalytics: true });
    }
  }, [testId, apiBaseUrl, doFetch]);

  /**
   * Submit feedback (thumbs up, thumbs down, or suggestion form).
   * @param {object} payload - e.g. { rating: 10 }, { rating: 1 }, or full suggestion payload
   */
  const submitFeedback = useCallback(
    async (payload) => {
      if (!testId || !apiBaseUrl) return;
      try {
        await submitRCAFeedback(
          apiBaseUrl,
          testId,
          product,
          payload,
          cookieName
        );
        return true;
      } catch {
        return false;
      }
    },
    [testId, apiBaseUrl, product, cookieName]
  );

  // Auto-fetch on mount if enabled (check for existing RCA, don't trigger generation)
  useEffect(() => {
    if (enabled && testId && apiBaseUrl) {
      fetchExistingRCA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, testId]);

  const dismissNotEnoughCredit = useCallback(() => {
    setIsNotEnoughCredit(false);
  }, []);

  return {
    rcaData,
    isLoading,
    isGenerating,
    isError,
    errorMessage,
    isNotEnoughCredit,
    dismissNotEnoughCredit,
    errorRepetitionData,
    errorTrendsData,
    triggerRCA,
    fetchExistingRCA,
    refetch,
    submitFeedback,
  };
};

export default useRCA;
